import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import Axios, { AxiosResponse } from 'axios';

import { ILogoStateInfo, LogoColorVariant, LogoSizeVariant, ILogoVersionInfo } from '../shared/logo.types';
import { ILogoProcessingResult } from '../logo-app/logo-upload/logo-upload.component';
import { DebuggerInstance } from '../shared/debugger';

import { DtpStepDownload } from './cards/dtp-step-download.component';
import { DtpStepReview } from './cards/dtp-step-review.component';
import { DtpStepUpload } from './cards/dpt-step-upload.component';
import { DtpStepLogoReview } from './cards/dtp-step-logo-review.component';
import { DtpStepRejectLogo } from './cards/dtp-step-reject-logo.component';
import { DtpStepCompleted } from './cards/dtp-step-completed.component';
import { DtpStepCheckingAccess } from './cards/dtp-step-checking-access.component';
import { DtpStepAccessDenied } from './cards/dtp-step-no-access.component';
import { DtpInstructions } from './cards/dtp-instructions.component';
import { DtpHeader } from './dtp-header.component';

import './dtp-portal.css';

export interface IDtpParams {
    logoId: string;
    hash: string;
}

export interface ILogoValidationResponse {
    success: boolean;
    downloadUrl?: string;
    eshopName?: string;
    message?: string;
}

export interface IDtpPortalControllerProps extends RouteComponentProps<IDtpParams> {

}

enum DtpSteps {
    None,
    Download,
    Review,
    Upload,
    UploadReview,
    Reject,
    Completed,
    CheckingAccess,
    AccessDenied,
}

export interface IDtpPortalControllerState {
    downloadUrl?: string;
    newLogoUploaded: boolean;
    newLogoInfo: ILogoStateInfo | null;
    step: DtpSteps;
    message?: string;
    eshopName?: string;
}


class DtpPortalControllerComponent extends React.PureComponent<IDtpPortalControllerProps, IDtpPortalControllerState>{
    private baseApiUrl: string = '/api/dtp/';

    constructor(props: IDtpPortalControllerProps) {
        super(props);

        this.state = {
            newLogoUploaded: false,
            newLogoInfo: null,
            step: DtpSteps.None
        };

        this.onUploadCompleted = this.onUploadCompleted.bind(this);
    }

    public componentDidMount() {
        DebuggerInstance.log('DtpPortalControllerComponent.componentDidMount');

        this.setState({
            step: DtpSteps.CheckingAccess
        });

        const apiUrl: string = `${this.baseApiUrl}validate/${this.props.match.params.logoId}/${this.props.match.params.hash}`;
        Axios.get<ILogoValidationResponse>(apiUrl)
            .then((val) => {
                DebuggerInstance.log('then');
                DebuggerInstance.log(val);

                this.setState({
                    downloadUrl: val.data.downloadUrl,
                    step: DtpSteps.Download,
                    eshopName: val.data.eshopName
                });
            })
            .catch((error) => {
                DebuggerInstance.log('catch');
                DebuggerInstance.log(error.response);

                const response = error.response as AxiosResponse<ILogoValidationResponse>;
                if (response && response.status >= 400 && response.status < 500) {
                    this.setState({
                        step: DtpSteps.AccessDenied,
                        message: response.data.message
                    });
                } else {
                    this.setState({
                        step: DtpSteps.AccessDenied,
                        message: 'Server Error'
                    });
                }
            })
    }

    private createLogoStateInfo(logoId: string): ILogoStateInfo {
        const api = this.baseApiUrl + `logo/${logoId}/${this.props.match.params.hash}`;
        const nocache = Date.now().toString(); // forces reload of images

        return {
            logoId,
            normal: {
                variant: LogoColorVariant.normal,
                src: api + '?color_variant=' + LogoColorVariant.normal + '&size_variant=' + LogoSizeVariant.Preview + '&nocache=' + nocache,
                isLoaded: false,
                accept: true,
            },
            white: {
                variant: LogoColorVariant.white,
                src: api + '?color_variant=' + LogoColorVariant.white + '&size_variant=' + LogoSizeVariant.Preview + '&nocache=' + nocache,
                isLoaded: false,
                accept: true,
            },
            black: {
                variant: LogoColorVariant.black,
                src: api + '?color_variant=' + LogoColorVariant.black + '&size_variant=' + LogoSizeVariant.Preview + '&nocache=' + nocache,
                isLoaded: false,
                accept: true,
            }
        };
    }

    private handleConfirmationChanged(version: ILogoVersionInfo, confirn: boolean): void {
        DebuggerInstance.log('handleConfirmationChanged');
        DebuggerInstance.log(version);
        DebuggerInstance.log(confirn);

        if (this.state.newLogoInfo === null) {
            throw new Error("Invalid state of application!");
        }

        const logo: ILogoStateInfo = Object.assign({}, this.state.newLogoInfo);
        switch (version.variant) {
            case LogoColorVariant.normal:
                logo.normal.accept = confirn;
                break;
            case LogoColorVariant.white:
                logo.white.accept = confirn;
                break;
            case LogoColorVariant.black:
                logo.black.accept = confirn;
                break;
        }
        this.setState({ newLogoInfo: logo });
        DebuggerInstance.log(this.state);
    }

    private handleLogoLoaded(version: ILogoVersionInfo, success: boolean): void {
        if (this.state.newLogoInfo === null) {
            throw new Error("Invalid state of application!");
        }

        const logo: ILogoStateInfo = this.state.newLogoInfo;
        switch (version.variant) {
            case LogoColorVariant.normal:
                logo.normal.isLoaded = success;
                break;
            case LogoColorVariant.white:
                logo.white.isLoaded = success;
                break;
            case LogoColorVariant.black:
                logo.black.isLoaded = success;
                break;
        }
        this.setState({ newLogoInfo: logo });
    }

    private onUploadCompleted(sucess: boolean, data: ILogoProcessingResult | null, message: string | null): void {
        DebuggerInstance.log('onUploadCompleted');
        DebuggerInstance.log(data);

        if (sucess && data != null) {
            this.setState({
                newLogoUploaded: true,
                newLogoInfo: this.createLogoStateInfo(data.logoId),
                step: DtpSteps.UploadReview
            });
        }
    }

    private handleDtpCompleted(): void {
        this.setState({
            step: DtpSteps.Completed
        });
    }

    private getDtpStepComponentFromState(): JSX.Element {
        switch (this.state.step) {
            case DtpSteps.Download:
                return <DtpStepDownload
                    downloadUrl={this.state.downloadUrl as string}
                    onNextClick={() => this.setState({ step: DtpSteps.Review })}
                />;
            case DtpSteps.Review:
                return <DtpStepReview
                    onBackClick={() => this.setState({ step: DtpSteps.Download })}
                    onAcceptClick={() => this.setState({ step: DtpSteps.Upload })}
                    onRejectClick={() => this.setState({ step: DtpSteps.Reject })}
                />
            case DtpSteps.Upload:
                return <DtpStepUpload
                    logoId={this.props.match.params.logoId}
                    hash={this.props.match.params.hash}
                    apiUrl={this.baseApiUrl}
                    onUploadCompleted={this.onUploadCompleted}
                    onBackClick={() => this.setState({ step: DtpSteps.Review })}
                />
            case DtpSteps.UploadReview:
                return <DtpStepLogoReview
                    logoInfo={this.state.newLogoInfo as ILogoStateInfo}
                    baseApiUrl={this.baseApiUrl}
                    hash={this.props.match.params.hash}
                    onLogoLoaded={(v, s) => this.handleLogoLoaded(v, s)}
                    onConfirmationChanged={(v, val) => this.handleConfirmationChanged(v, val)}
                    onDtpCompleted={() => this.handleDtpCompleted()}
                    onRepeatUpload={() => this.setState({ step: DtpSteps.Upload })}
                    onRejectLogo={() => { console.log('todo') }}
                    onUpdateImages={() => this.handleUpdateImages()}
                />;
            case DtpSteps.Reject:
                return <DtpStepRejectLogo
                    logoId={this.props.match.params.logoId}
                    baseApiUrl={this.baseApiUrl}
                    hash={this.props.match.params.hash}
                    onDtpCompleted={() => this.handleDtpCompleted()}
                    onBackClick={() => this.setState({ step: DtpSteps.Review })}
                />;
            case DtpSteps.Completed:
                return <DtpStepCompleted />;
            case DtpSteps.CheckingAccess:
                return <DtpStepCheckingAccess />;
            case DtpSteps.AccessDenied:
                return <DtpStepAccessDenied
                    message={this.state.message || 'Access denied!'}
                />;
            default:
                break;
        }

        return <div></div>
    }

    private shouldShowInstructions(): boolean {
        return this.state.step === DtpSteps.Download;
    }

    private handleUpdateImages() {
        if (this.state.newLogoInfo && this.state.newLogoInfo.logoId) {
            this.setState({
                newLogoInfo: this.createLogoStateInfo(this.state.newLogoInfo.logoId)
            });
        }
    }

    public render() {
        return (
            <div className="dpt-portal-root">
                <DtpHeader mainTitle={"PromoTron DTP Portal"} subTitle={"Manual Logo Prepare Tool"} />
                {this.shouldShowInstructions() && <DtpInstructions eshopName={this.state.eshopName} />}
                {this.getDtpStepComponentFromState()}
            </div>
        );
    }
}

export const DtpPortalController = (props: IDtpPortalControllerProps) => <DtpPortalControllerComponent {...props} />



// WEBPACK FOOTER //
// ./src/App/dtp-portal/dtp-portal.controller.tsx